import * as THREE from 'three';
import HSBControls,{  HSBControlsEventTypes } from './Camera/HSBControls';
import { HsbEntity, SceneLayerName } from '../hsbcad-model';
import { ViewerLayers, ViewerLayersBit } from './Enums/ViewerLayers';
import { Picker } from './Tools/Picker';

const units = [0.0001, 100000];


/**
 * Main scene object. Consists of THREE.Scene + additional fuctionality.
 * @extends THREE.Scene
 */
export class ViewerScene extends THREE.Scene {

    //Grid
    _grid: THREE.GridHelper | null = null;

    // Wireframe
    private _wireframe: THREE.Mesh | null = null;
    public get WireFrame(): THREE.Mesh | null {
        return this._wireframe;
    }
    public set WireFrame(v: THREE.Mesh | null) {
        this._wireframe = v;
    }

    // Cameras
    currentCamera: THREE.OrthographicCamera | THREE.PerspectiveCamera | null;
    _perspectiveCamera: THREE.PerspectiveCamera ;
    _orthographicCamera: THREE.OrthographicCamera ;
    _camerPivot: THREE.Mesh | null = null;


    // Controls
    public get CameraControls() {
        return this._cameraControls;
    }
    private _cameraControls: HSBControls | null = null;

    // Objects
    private _light: THREE.AmbientLight | null = null;
    public get light(): THREE.AmbientLight | null{
        return this._light;
    }
    public set light(v: THREE.AmbientLight | null) {
        this._light = v;
    }

    // Hsb
    _hsbLayers: { [key: string]: any };

    // Pivot Point
    _pivotPoint: THREE.Object3D | null = null;

    //Solid or Ghost
    _showSolid: boolean = true;



    constructor(width: number, height: number, renderer: THREE.WebGLRenderer, options?: {picker?: Picker, unit?: 'string'}) {
        super();
        // Cameras
        this._perspectiveCamera = new THREE.PerspectiveCamera(50, width / height, 1, 10000);
        const aspect = width / height;
        this._perspectiveCamera.aspect = aspect;
        this._perspectiveCamera.updateProjectionMatrix();
        this._perspectiveCamera.layers.enableAll();
        this._perspectiveCamera.layers.disable(ViewerLayers.ISSUES);
        this.add(this._perspectiveCamera);
        this._orthographicCamera = new THREE.OrthographicCamera(width * -0.5, width * 0.5, height * 0.5, height * -0.5, 0.0001, 100000);
        this._orthographicCamera.layers.enableAll();
        this._orthographicCamera.layers.disable( ViewerLayers.ISSUES);
        this.currentCamera = this._orthographicCamera;
        this.add(this._orthographicCamera);



        // Controls
        this._cameraControls = new HSBControls(this.currentCamera, renderer.domElement, options?.picker);
        this._cameraControls.zoomToCursor = true;
        this._cameraControls.pivotMethod = 'click';
        this._cameraControls.setPivotLayers(ViewerLayers.MODELS);
        this._cameraControls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.DOLLY };
        this._cameraControls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.PAN }
        this._cameraControls.maxPolarAngle = Math.PI;

        
        this.currentCamera.layers.disable(ViewerLayers.SIMPLEMODEL);

        this._hsbLayers = {};
        let entityLayer = this._hsbLayers[SceneLayerName.Entities] = new THREE.Object3D();
        entityLayer.name = SceneLayerName.Entities;
        this.add(entityLayer);
    }

    

    /**
     * Handles the resize for the cameras in this scene
     * @param aspect The new aspect ratio after the resizing.
     */
    handleResize(aspect: number, newDimensions: THREE.Vector2, oldDimensions: THREE.Vector2) {
        this._perspectiveCamera.aspect = aspect;
        this._perspectiveCamera.updateProjectionMatrix();

        let diffX = newDimensions.x / oldDimensions.x;
        let diffY = newDimensions.y / oldDimensions.y;
        this._orthographicCamera.bottom *= diffY;
        this._orthographicCamera.top *= diffY;
        this._orthographicCamera.left *= diffX;
        this._orthographicCamera.right *= diffX;
        this._orthographicCamera.updateProjectionMatrix();
    }

    /**
     * Add listeners for the camera change event.
     * @param method The method to invoke for this event
     */
    AddCameraListeners(type:HSBControlsEventTypes,method: (event: any) => void) {
        this._cameraControls?.addEventListener(type, (event) => {
            method(event);
        });
    }

    /**
     * Remove listeners for the camera change event
     * @param method The method to remove as a listener.
     */
    RemoveCameraListeners(type:HSBControlsEventTypes,method: (event: any) => void) {
        this._cameraControls?.removeEventListener(type, (event) => {
            method(event);
        });

    }

    // ----- CAMERAS -----
    /**
     * Switches to an orthographic camera
     */
    public SetCameraOrthoGraphic() {
        if (this.currentCamera === this._orthographicCamera)
            return;

        this.SetCamera(this._orthographicCamera);
    }

    /**
     * Switches to a perspective camera
     */
    public SetCameraPerspective() {
        if (this.currentCamera === this._perspectiveCamera)
            return;

        this.SetCamera(this._perspectiveCamera);
    }

    /**
     * Sets the camera to the given THREE.Camera
     * @param camera The camera to be used
     */
    public SetCamera(camera: THREE.OrthographicCamera | THREE.PerspectiveCamera) {
        if (this.currentCamera) {
            camera.position.copy(this.currentCamera.position);
            camera.matrix.copy(this.currentCamera.matrix);
            camera.layers = this.currentCamera.layers;
            camera.zoom = this.currentCamera.zoom;
        }
        this.currentCamera = camera;
        if (this._cameraControls)
            this._cameraControls.object = this.currentCamera;

        this.currentCamera.updateMatrix();
    }

    /**
     * Toggles the visibility of the grid. Creates the grid if there was none.
     */
    public ToggleGrid(state: boolean) {
        if (state) {
            if (!this._grid) {
                const size = 5000;
                const divisions = 10;
                const color = new THREE.Color(0xbbbbbb)
                this._grid = new THREE.GridHelper(size, divisions, color);
                //this._grid.layers.disableAll();
                this._grid.layers.set(ViewerLayers.INFO);
                this._grid.up = this.up;
                this.add(this._grid);
            }
            else {
                this._grid.visible = state;
            }
        }
        else {
            if (this._grid)
                this._grid.visible = state;
        }


    }




    /**
     * Returns the desired layer object
     * @param layer The name of the layer. Use children of SceneLayerName
     */
    public GetLayer(layer: string): THREE.Object3D {
        return this._hsbLayers[layer];
    }

    /**
     * Sets the desired layer object
     * @param layer The name of the layer. Use children of SceneLayerName
     * @param object The object to set as root for this layer.
     */
    public SetLayer(layer: string, object: THREE.Object3D) {
        return this._hsbLayers[layer] = object;
    }

    /**
     * Not Implemented
     * @param zones 
     */
    showZone(...zones: number[]) {
        
    }

    /**
     * Not Implemented
     * @param zones 
     */
    hideZone(...zones: number[]) {
        
    }

    setLightIntensity(value: number) {
        if (this.light)
            this._light!.intensity = Math.min(1, Math.max(0, value));
    }

    getAllObjectForLayer(layerName: SceneLayerName) {
        let out: THREE.Object3D[] = [];
        let layerRoot: THREE.Object3D = this._hsbLayers[layerName];
        layerRoot?.traverse((object) => {
           out.push(object);
        });
        return out;
    }

    /**
     * Methods to perform when disposing this scene.
     */
    onDispose() {
        let self = this;
        this._cameraControls?.dispose();
        [this._wireframe, this._grid, this._orthographicCamera, this._perspectiveCamera, this._pivotPoint].forEach((object: any) => {
            this.remove(object);
            object?.geometry?.dispose();
            object?.texture?.dispose();
        })
        this._wireframe = null;
        this._grid = null;
        this._pivotPoint = null;
        this._cameraControls = null;
        this.light = null;
    }
}